import { useContext, useEffect, useState } from 'react';
import usePmSensorAuthId from 'components/hooks/PM/usePmSensorAuthId';
import _ from 'lodash';
import { getRoundedFte } from '../../../ResourceAllocationOverview/resource-allocation.formatters';
import { InvestmentCarvingFiltersContext } from '../../../state/filters/contexts';
import useResourceAllocationService from 'common/services/resource-allocation.service.versions';
import { useDebounceCallback } from '@react-hook/debounce';

export const CATEGORIES_KEYS = {
  KTLO: 'ktlo',
  DEVELOPER_EXPERIENCE: 'developer_experience',
  FEATURE_ENHANCEMENTS: 'feature_enhancements',
  NEW_VALUE: 'new_value',
  OTHERS: 'others',
  POTENTIAL_SAVINGS: 'inefficiency_pool'
};

const useCategoriesWidget = () => {
  const {
    state: { period, filtersPayload, extraPayload, isInitialized }
  } = useContext(InvestmentCarvingFiltersContext);
  const raService = useResourceAllocationService();
  const pmSensorAuthId = usePmSensorAuthId();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [widgetData, setWidgetData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const categories = [...(widgetData?.categories ?? [])];
  const orderedCategories = _.orderBy(categories, ['ui_order'], ['asc']);

  const generateCategory = ({ key, name, fte, fte_p, benchmark, color }) => ({
    key,
    order: categories.length + 1,
    name,
    color,
    fte,
    fte_p,
    benchmark
  });

  const potentialSavings = widgetData?.[CATEGORIES_KEYS.POTENTIAL_SAVINGS];
  if (potentialSavings?.enabled) {
    orderedCategories.push(generateCategory(potentialSavings));
  }

  const others = widgetData?.[CATEGORIES_KEYS.OTHERS];
  if (!!others?.fte_p) {
    orderedCategories.push(generateCategory(others));
  }

  const initSelectedCategory = () => {
    const updatedSelectedCategory =
      categories.find(category => category.name === selectedCategory?.name) ||
      orderedCategories[0];

    setSelectedCategory(updatedSelectedCategory);
  };

  const fetchWidgetCategories = useDebounceCallback(async () => {
    try {
      setIsLoading(true);
      const widgetRes = await raService.getInvestmentCarving({
        sensor_authorization_id: pmSensorAuthId,
        before: period.before,
        after: period.after,
        filtersPayload,
        extraPayload
      });
      setWidgetData(widgetRes);
    } catch (e) {
      setWidgetData(null);
    } finally {
      setIsLoading(false);
    }
  }, 500);

  useEffect(() => {
    if (!isInitialized) return;
    fetchWidgetCategories();
  }, [isInitialized, period.before, period.after, filtersPayload]);

  useEffect(() => {
    if (!!widgetData) {
      initSelectedCategory();
    }
  }, [widgetData]);

  const categoriesWithRoundedData = orderedCategories.map(category => ({
    ...category,
    fte_p: category.fte_p,
    round_fte_p: getRoundedFte(category.fte_p),
    fte: getRoundedFte(category.fte)
  }));

  return {
    categories: categoriesWithRoundedData,
    selectedCategory,
    setSelectedCategory,
    isLoading
  };
};

export default useCategoriesWidget;
